import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, ThemeProvider } from "@mui/material";
import { Parcel } from "../../../model/parcel";
import { VPUITheme } from "../../../../common/theme/vpui-theme";
import { ParcelStockoutScanner } from "./parcel-stockout-scanner";
import { ParcelStockoutProductSelection } from "./parcel-stockout-product-selection";

import "./style.scss";

interface IParcelStockoutDialogProps {
    parcelsList: Parcel[];
    getParcelItemByScan: (locator: string) => Parcel | undefined;
    onParcelChanged: () => void;
    onCancel: () => void;
}

export const ParcelStockoutDialog = (props: IParcelStockoutDialogProps) => {
    const isScanMode = useMemo(() => props.parcelsList.length === 0, [props.parcelsList]);

    return <Dialog open={true} onClose={props.onCancel} slotProps={{ paper: { sx: { overflow: "visible" }}}}>
        <ThemeProvider theme={VPUITheme}>
            {isScanMode ?
                <ParcelStockoutDialogScanFlow getParcelItemByScan={props.getParcelItemByScan}
                                              onParcelChanged={props.onParcelChanged}
                                              onCancel={props.onCancel} /> :
                <ParcelStockoutDialogParcelStockoutSelectionFlow parcelsList={props.parcelsList}
                                                                 onParcelChanged={props.onParcelChanged}
                                                                 onCancel={props.onCancel} />}
        </ThemeProvider>
    </Dialog>;
};

const ParcelStockoutDialogScanFlow = (
    { getParcelItemByScan, onParcelChanged, onCancel }: {
        getParcelItemByScan: (locator: string) => Parcel | undefined,
        onParcelChanged: () => void,
        onCancel: () => void,
    }) => {
    const [scannedParcel, setScannedParcel] = useState<Parcel | undefined>();

    const handleParcelFinished = useCallback(() => {
        setScannedParcel(undefined);
        onParcelChanged();
    }, []);

    return !scannedParcel ?
        <ParcelStockoutScanner getParcelByLocator={getParcelItemByScan}
                               onItemFound={item => setScannedParcel(item)}
                               onCancel={onCancel} /> :
        <ParcelStockoutProductSelection key={`parcel-${scannedParcel.parcelId}`}
                                        parcel={scannedParcel}
                                        hasNext={true}
                                        onParcelCompleted={handleParcelFinished}
                                        onCancel={onCancel} />;
};

const ParcelStockoutDialogParcelStockoutSelectionFlow = (
    { parcelsList, onParcelChanged, onCancel }: {
        parcelsList: Parcel[],
        onParcelChanged: () => void,
        onCancel: () => void,
    }) => {
    const [currentParcel, setCurrentParcel] = useState<Parcel>(parcelsList[0]);
    const [parcelQueue, setParcelQueue] = useState<Parcel[]>(parcelsList);

    const handleParcelFinished = useCallback((parcel: Parcel) => {
        setParcelQueue(prevState => prevState.filter(p => p.id !== parcel.id));
        onParcelChanged();
    }, []);

    useEffect(() => {
        if (parcelQueue.length === 0) {
            onCancel();
        }

        setCurrentParcel(parcelQueue[0]);
    }, [parcelQueue]);

    return <ParcelStockoutProductSelection key={`parcel-${currentParcel.parcelId}`}
                                           parcel={currentParcel}
                                           hasNext={parcelQueue.length > 1}
                                           onParcelCompleted={handleParcelFinished}
                                           onCancel={onCancel} />;
};
